/**
 * Runs a video export for the dialog: owns the AbortController (published on
 * videoStore so anything can cancel), progress, the finished file's object
 * URL, and saving the result to the project's reels.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProjectPage, VideoExportSettings, VideoProject } from '@canvas/shared-types';
import { downloadBlob } from '@/lib/createKenBurnsReel';
import {
  canExportNow,
  fetchExportQuota,
  recordExportSuccess,
  SUBSCRIPTIONS_ENABLED,
} from '@/lib/exportLimits';
import { IDLE_REEL_PROGRESS, type ReelProgress } from '@/lib/reelProgress';
import { persistLocalReel, type StudioReel } from '@/lib/reelStore';
import type { EncoderPlan } from '@/lib/video/encoderCapabilities';
import { describeExportError, isAbortError } from '@/lib/video/exportErrors';
import type { EncodedVideoResult } from '@/lib/video/videoEncoder';
import { exportVideo } from '@/lib/video/videoExport';
import { videoProgressAt } from '@/lib/video/videoProgress';
import { useVideoStore } from '@/stores/videoStore';

export interface FinishedVideo extends EncodedVideoResult {
  url: string;
}

export function useVideoExport() {
  const [progress, setProgress] = useState<ReelProgress>(IDLE_REEL_PROGRESS);
  const [result, setResult] = useState<FinishedVideo | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<StudioReel | null>(null);
  const running = useVideoStore((s) => s.exportController !== null);
  const urlRef = useRef<string | null>(null);

  const releaseUrl = () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  };

  const cancel = useCallback(() => {
    useVideoStore.getState().exportController?.abort();
  }, []);

  const reset = useCallback(() => {
    cancel();
    releaseUrl();
    setResult(null);
    setSaved(null);
    setProgress(IDLE_REEL_PROGRESS);
  }, [cancel]);

  useEffect(() => () => reset(), [reset]);

  const start = useCallback(
    async (args: {
      pages: ProjectPage[];
      video: VideoProject;
      settings: VideoExportSettings;
      plan?: EncoderPlan;
      fileHandle?: FileSystemFileHandle;
    }) => {
      const store = useVideoStore.getState();
      if (store.exportController) return;
      reset();

      // TODO(subscription): video exports count toward the monthly quota once Pro ships.
      if (SUBSCRIPTIONS_ENABLED) {
        const gate = canExportNow(await fetchExportQuota());
        if (!gate.ok) {
          setProgress(videoProgressAt('failed', { error: gate.reason, label: 'Export limit reached' }));
          return;
        }
      }

      const controller = new AbortController();
      store.setExportController(controller);
      try {
        const encoded = await exportVideo({
          pages: args.pages,
          video: args.video,
          settings: args.settings,
          plan: args.plan,
          fileHandle: args.fileHandle,
          signal: controller.signal,
          onProgress: setProgress,
        });
        const url = URL.createObjectURL(encoded.blob);
        urlRef.current = url;
        setResult({ ...encoded, url });
        setProgress(videoProgressAt('ready', { label: 'Video ready' }));
        void recordExportSuccess();
      } catch (err) {
        setProgress(
          isAbortError(err)
            ? IDLE_REEL_PROGRESS
            : videoProgressAt('failed', { label: 'Export failed', error: describeExportError(err) })
        );
      } finally {
        if (useVideoStore.getState().exportController === controller) {
          useVideoStore.getState().setExportController(null);
        }
      }
    },
    [reset]
  );

  const download = useCallback((name: string) => {
    if (!result) return;
    const ext = result.mimeType.includes('mp4') ? 'mp4' : 'webm';
    downloadBlob(result.blob, `${name || 'craftara-video'}.${ext}`);
  }, [result]);

  /** Upload to the project's reels so it shows up in Photo Studio and survives refresh. */
  const saveToProject = useCallback(
    async (projectId: string, platformId?: string): Promise<string | null> => {
      if (!result || saving) return null;
      setSaving(true);
      try {
        const reel = await persistLocalReel({
          projectId,
          blob: result.blob,
          platformId: platformId === 'custom' ? undefined : platformId,
          motionStyleId: 'motion-timeline',
          width: result.width,
          height: result.height,
          durationSec: Math.round(result.durationMs / 100) / 10,
        });
        setSaved(reel);
        return null;
      } catch (err) {
        return describeExportError(err);
      } finally {
        setSaving(false);
      }
    },
    [result, saving]
  );

  return { progress, result, running, saving, saved, start, cancel, reset, download, saveToProject };
}
